import React, { useCallback, useEffect, useState } from "react";
import { RefreshCw, Video, Loader2, ExternalLink } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { AdminSalesMap } from "./AdminSalesMap";

const DIAG_STYLE = {
  paid: "bg-success/10 text-success",
  technical_error: "bg-destructive/10 text-destructive",
  payment_started: "bg-amber-500/10 text-amber-500",
  abandoned: "bg-muted text-muted-foreground",
};
const DIAG_LABEL = {
  paid: "✅ Payée",
  technical_error: "⚠️ Erreur technique",
  payment_started: "🕐 Paiement non fini",
  abandoned: "👻 Abandon",
};

const flagEmoji = (cc) => {
  if (!cc || cc.length !== 2) return "";
  try { return String.fromCodePoint(...cc.toUpperCase().split("").map((c) => 127397 + c.charCodeAt(0))); } catch { return ""; }
};

export default function AdminFunnelTab() {
  const { language } = useI18n();
  const [days, setDays] = useState(1);
  const [data, setData] = useState(null);
  const [openRow, setOpenRow] = useState(null);
  const [activity, setActivity] = useState({});
  const [loadingRow, setLoadingRow] = useState(null);

  const load = useCallback(() => {
    api.get(`/admin/conversion-funnel?days=${days}`).then(({ data }) => setData(data)).catch(() => {});
  }, [days]);

  const toggleActivity = async (row) => {
    if (openRow === row.order_id) { setOpenRow(null); return; }
    setOpenRow(row.order_id);
    if (!activity[row.email]) {
      setLoadingRow(row.order_id);
      try {
        const { data } = await api.get(`/admin/posthog/user-activity?email=${encodeURIComponent(row.email)}&around=${encodeURIComponent(row.created_at || "")}`);
        setActivity((a) => ({ ...a, [row.email]: data }));
      } catch (e) {
        setActivity((a) => ({ ...a, [row.email]: { error: e?.response?.data?.detail || "Erreur PostHog" } }));
      } finally {
        setLoadingRow(null);
      }
    }
  };

  // Mise à jour automatique : au chargement + toutes les 30 secondes.
  useEffect(() => {
    load();
    const i = setInterval(load, 30000);
    return () => clearInterval(i);
  }, [load]);

  if (!data) return <p className="p-8 text-sm text-muted-foreground">Chargement...</p>;
  const s = data.summary;
  const fmt = (d) => new Date(d).toLocaleString(language === "fr" ? "fr-FR" : "en-US", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });

  return (
    <div className="space-y-5" data-testid="admin-funnel-tab">
      <div className="flex flex-wrap items-center gap-2">
        {[1, 7, 30].map((d) => (
          <button key={d} onClick={() => setDays(d)} data-testid={`funnel-days-${d}`}
            className={`rounded-full px-4 py-1.5 text-xs font-semibold ${days === d ? "bg-primary text-primary-foreground" : "border border-border text-muted-foreground"}`}>
            {d === 1 ? "Aujourd'hui" : `${d} jours`}
          </button>
        ))}
        <button onClick={load} data-testid="funnel-refresh" className="ml-auto flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground">
          <RefreshCw className="h-3.5 w-3.5" /> Actualiser
        </button>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div className="rounded-xl border border-success/30 bg-success/5 p-4">
          <p className="text-xs text-muted-foreground">Encaissé (commandes + paiements)</p>
          <p className="font-heading text-xl font-semibold text-success" data-testid="funnel-collected">
            {(s.orders_paid_sum + s.payments_paid_sum + (s.subs_gp_paid_sum || 0)).toFixed(2)}€
          </p>
          <p className="text-[10px] text-muted-foreground">{s.orders_paid_count} commandes payées · abos actifs : {s.subs_active}</p>
        </div>
        <div className="rounded-xl border border-primary/30 bg-primary/5 p-4">
          <p className="text-xs text-muted-foreground">Potentiel non converti</p>
          <p className="font-heading text-xl font-semibold text-primary" data-testid="funnel-missed">{s.orders_pending_sum.toFixed(2)}€</p>
          <p className="text-[10px] text-muted-foreground">{s.orders_pending_count} commandes en attente</p>
        </div>
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4">
          <p className="text-xs text-muted-foreground">Autres paiements non finis</p>
          <p className="font-heading text-xl font-semibold text-amber-500">{s.topups_open_sum.toFixed(2)}€</p>
          <p className="text-[10px] text-muted-foreground">{s.topups_open_count} tentatives Google Play</p>
        </div>
        <div className="rounded-xl border border-border bg-card p-4">
          <p className="text-xs text-muted-foreground">Erreurs appareil / abos en attente</p>
          <p className="font-heading text-xl font-semibold">{s.errors_count} / {s.subs_pending}</p>
        </div>
      </div>

      <AdminSalesMap days={days} />

      <div className="overflow-x-auto rounded-xl border border-border">
        <table className="w-full text-left text-xs">
          <thead className="bg-muted/50 text-muted-foreground">
            <tr>
              <th className="p-2.5">Date</th><th className="p-2.5">Commande</th><th className="p-2.5">Client</th>
              <th className="p-2.5">Pays</th>
              <th className="p-2.5">Prix (€)</th><th className="p-2.5">Achat réel (€)</th><th className="p-2.5">Diagnostic</th><th className="p-2.5">Pourquoi (explication)</th><th className="p-2.5">Sessions</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r) => (
              <React.Fragment key={r.order_id}>
                <tr className="border-t border-border" data-testid={`funnel-row-${r.order_id}`}>
                  <td className="whitespace-nowrap p-2.5">{fmt(r.created_at)}</td>
                  <td className="p-2.5 font-mono text-[10px]">{r.order_id}{r.kind === "subscription" && <span className="ml-1 rounded bg-primary/10 px-1 font-sans text-[9px] font-semibold text-primary">ABO</span>}</td>
                  <td className="max-w-[160px] truncate p-2.5">{r.email || "—"}</td>
                  <td className="whitespace-nowrap p-2.5" data-testid={`funnel-country-${r.order_id}`}>
                    {(r.country || r.country_code) ? (
                      <div>
                        <p>{flagEmoji(r.country_code)} {r.country || r.country_code}</p>
                        <p className="text-[10px] text-muted-foreground">
                          {[r.city, r.os, r.browser_lang].filter(Boolean).join(" · ")}
                        </p>
                      </div>
                    ) : "—"}
                  </td>
                <td className="whitespace-nowrap p-2.5 font-semibold">{r.total != null ? (r.kind === "subscription" ? `${r.total}€/mois` : `${r.total}€`) : "—"}{r.promo && <span className="ml-1 rounded bg-primary/10 px-1 text-[9px] text-primary">PROMO</span>}</td>
                  <td className="whitespace-nowrap p-2.5 text-muted-foreground">{r.payment || "—"}</td>
                  <td className="whitespace-nowrap p-2.5"><span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${DIAG_STYLE[r.diagnosis]}`}>{DIAG_LABEL[r.diagnosis]}</span></td>
                  <td className="min-w-[280px] max-w-[420px] p-2.5 text-muted-foreground">{r.detail}</td>
                  <td className="p-2.5">
                    {r.email ? (
                      <button onClick={() => toggleActivity(r)} data-testid={`funnel-sessions-${r.order_id}`}
                        className={`flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${openRow === r.order_id ? "border-primary text-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>
                        {loadingRow === r.order_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Video className="h-3 w-3" />} Voir
                      </button>
                    ) : "—"}
                  </td>
                </tr>
                {openRow === r.order_id && (
                  <tr className="border-t border-border bg-muted/30" data-testid={`funnel-activity-${r.order_id}`}>
                    <td colSpan="9" className="p-3">
                      {loadingRow === r.order_id && <p className="text-muted-foreground">Chargement PostHog...</p>}
                      {activity[r.email]?.error && <p className="text-destructive">{activity[r.email].error}</p>}
                      {activity[r.email] && !activity[r.email].error && (
                        <div className="grid gap-3 sm:grid-cols-2">
                          <div>
                            <p className="mb-1.5 font-semibold">🎥 Replays de session ({activity[r.email].recordings.length || activity[r.email].candidates?.length || 0})</p>
                            {activity[r.email].recordings.length ? activity[r.email].recordings.map((rec) => (
                              <a key={rec.id} href={rec.replay_url} target="_blank" rel="noreferrer"
                                className="mb-1 flex items-center gap-1.5 text-primary hover:underline">
                                <ExternalLink className="h-3 w-3" />
                                {fmt(rec.start_time)} · {rec.duration_s ? `${Math.round(rec.duration_s)}s` : "?"} · {rec.start_url ? new URL(rec.start_url).pathname : ""}
                              </a>
                            )) : activity[r.email].candidates?.length ? (
                              <>
                                <p className="mb-1 text-[10px] text-amber-600 dark:text-amber-400">Session non liée au compte (ancienne visite anonyme) — voici les sessions enregistrées autour de cette commande, l'une d'elles est probablement ce client :</p>
                                {activity[r.email].candidates.map((rec) => (
                                  <a key={rec.id} href={rec.replay_url} target="_blank" rel="noreferrer"
                                    className="mb-1 flex items-center gap-1.5 text-primary hover:underline">
                                    <ExternalLink className="h-3 w-3" />
                                    {fmt(rec.start_time)} · {rec.duration_s ? `${Math.round(rec.duration_s)}s` : "?"} · {rec.start_url ? new URL(rec.start_url).pathname : ""}
                                  </a>
                                ))}
                              </>
                            ) : <p className="text-muted-foreground">Aucun replay trouvé pour ce client (sessions anonymes non liées, ou enregistrement expiré).</p>}
                          </div>
                          <div>
                            <p className="mb-1.5 font-semibold">⚡ Derniers événements ({activity[r.email].events.length})</p>
                            <div className="max-h-40 overflow-y-auto">
                              {activity[r.email].events.length ? activity[r.email].events.map((ev, i) => (
                                <p key={i} className="text-muted-foreground">{ev.timestamp?.slice(5, 16).replace("T", " ")} · <span className="text-foreground">{ev.event}</span>{ev.url ? ` · ${(() => { try { return new URL(ev.url).pathname; } catch { return ""; } })()}` : ""}</p>
                              )) : <p className="text-muted-foreground">Aucun événement récent.</p>}
                            </div>
                          </div>
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
            {!data.rows.length && <tr><td colSpan="9" className="p-6 text-center text-muted-foreground">Aucune commande sur la période</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
